/**
 * Satu dialog untuk lima belas operasi kurva.
 *
 * Menu QC & Edit Data, Precalculation dan Environment Corrections menjalankan
 * pekerjaan yang bentuknya sama: ambil satu-dua kurva, hitung sesuatu, tulis
 * hasilnya sebagai versi baru dari set asalnya. Yang berbeda hanyalah daftar
 * parameternya — dan daftar itu datang dari server, bukan ditulis ulang di
 * sini. Menyalinnya ke TypeScript berarti dua daftar yang harus tetap sama,
 * dan yang kedua akan tertinggal pada koefisien pertama yang ditambahkan.
 *
 * Setiap perubahan parameter memicu pratinjau: hitungannya dijalankan tanpa
 * menyimpan apa pun, dan hasilnya digambar di atas kurva yang sekarang. Itulah
 * inti layar ini. Ambang despike bukan angka yang bisa dipilih dari ingatan;
 * ia dipilih dengan melihat mana yang hilang dan mana yang bertahan pada kurva
 * yang sedang dikerjakan.
 */
import { useEffect, useMemo, useState } from "react";

import { api } from "../api";
import { canCompute } from "../auth";
import { useAsync, useDebounced } from "../hooks";
import type {
  CurveOpCatalogue,
  CurveOpField,
  CurveOpOutput,
  CurveOpResult,
  CurveOperation,
  Project,
  Well,
} from "../types";
import {
  Button,
  Checkbox,
  Dropdown,
  Empty,
  KeyValue,
  Loading,
  Modal,
  Notice,
  NumberField,
  OptionalNumberField,
  Panel,
  TextField,
  useToast,
} from "./ui";

type Values = Record<string, unknown>;
type Tie = { from: string; to: string };

/** `12:GR` — kurva disebut bersama setnya, sebab satu sumur lazim punya dua GR. */
interface CurveChoice {
  value: string;
  label: string;
  mnemonic: string;
}

function curveChoices(well: Well): CurveChoice[] {
  // Raw sets first, then active before superseded. The order decides what a
  // field opens on, and these menus edit measurements: a despike offered on a
  // derived BVW before it is offered on GR is offering the wrong thing.
  const ordered = [...well.curve_sets].sort((a, b) => {
    const raw = Number(b.kind === "raw") - Number(a.kind === "raw");
    if (raw) return raw;
    const active = Number(b.is_active) - Number(a.is_active);
    if (active) return active;
    return a.name.localeCompare(b.name) || b.version - a.version;
  });
  const choices: CurveChoice[] = [];
  for (const set of ordered) {
    for (const mnemonic of set.mnemonics) {
      choices.push({
        value: `${set.id}:${mnemonic}`,
        label: `${mnemonic} · ${set.name} v${set.version}`,
        mnemonic,
      });
    }
  }
  return choices;
}

/**
 * Kolom wajib yang masih kosong.
 *
 * Dipakai untuk menahan pratinjau. Dialog kalkulator yang terbuka dengan
 * "Rumus harus diisi." berwarna merah memarahi orang atas kelalaian yang belum
 * sempat mereka lakukan; yang benar adalah menunggu sampai ada yang bisa
 * dihitung, lalu menghitungnya.
 */
function missingRequired(
  operation: CurveOperation,
  values: Values,
  choices: CurveChoice[] = [],
): string[] {
  const missing: string[] = [];
  for (const field of operation.fields) {
    if (!field.required || !visible(field, values)) continue;
    const value = values[field.name];
    const empty = Array.isArray(value)
      ? value.length < Math.max(1, field.min_items ?? 1)
      : value === null || value === undefined || String(value).trim() === "";
    if (!empty) continue;
    if (Array.isArray(value) && (field.min_items ?? 1) > 1) {
      missing.push(`${field.label} — at least ${field.min_items} curves`);
      continue;
    }
    const absent =
      field.prefer?.length &&
      !field.prefer.some((wanted) => choices.some((choice) => choice.mnemonic === wanted));
    missing.push(absent ? `${field.label} — no ${field.prefer!.join(" / ")} on this well`
                        : field.label);
  }
  return missing;
}

/**
 * Kurva pertama yang masuk akal untuk sebuah kolom.
 *
 * Kolom "Kurva GR" yang terbuka berisi BVW hanya akan membuat orang menutupnya
 * dan memilih ulang, jadi server menyebutkan mnemonik yang dicari lebih dulu.
 *
 * Bila kolom itu menyebut mnemonik tertentu dan tak satu pun ada di sumur ini,
 * kolomnya dibiarkan kosong. Mundur ke "kurva pertama yang ada" pernah membuat
 * kolom Δρ terisi caliper, dan koreksi densitas yang keluar — 2,09 sampai 12,31
 * g/cc — terbaca seperti hasil hitungan biasa. Kosong itu jujur; angka yang
 * salah dengan grafik di sebelahnya tidak.
 */
function preferredCurve(field: CurveOpField, choices: CurveChoice[]): string {
  for (const wanted of field.prefer ?? []) {
    const found = choices.find((choice) => choice.mnemonic === wanted);
    if (found) return found.value;
  }
  return field.prefer?.length ? "" : (choices[0]?.value ?? "");
}

function initialValues(operation: CurveOperation, choices: CurveChoice[]): Values {
  const values: Values = {};
  // Curves already spoken for by an earlier field. Log reconstruction has a
  // target and a list of predictors, and seeding both with the first curve
  // opens the dialog regressing CALI on CALI: R² of 1, RMSE of 0, and nothing
  // learned. A distinct curve is not necessarily the right predictor, but it
  // is at least a question rather than a tautology.
  const taken = new Set<string>();
  for (const field of operation.fields) {
    if (field.type === "curve") {
      const picked = preferredCurve(field, choices);
      if (picked) taken.add(picked);
      values[field.name] = picked;
    } else if (field.type === "curves") {
      const wanted = Math.max(1, field.min_items ?? 1);
      const seeded = choices
        .filter((choice) => !taken.has(choice.value))
        .slice(0, wanted)
        .map((choice) => choice.value);
      seeded.forEach((value) => taken.add(value));
      values[field.name] = seeded;
    } else if (field.type === "ties") values[field.name] = [{ from: "", to: "" }];
    else if (field.type === "choice" && !choices.length && field.default_without_curves) {
      // Curve insert opens on a typed depth range where there is no curve to
      // take one from. Offering the template path first on a planned well
      // would open the dialog on a dropdown with nothing in it.
      values[field.name] = field.default_without_curves;
    } else values[field.name] = field.default ?? (field.type === "text" ? "" : null);
  }
  return values;
}

/** Whether the operation, as currently configured, needs a curve that exists. */
function needsExistingCurve(operation: CurveOperation, values: Values): boolean {
  return operation.fields.some(
    (field) =>
      (field.type === "curve" || field.type === "curves") &&
      field.required &&
      visible(field, values),
  );
}

function visible(field: CurveOpField, values: Values): boolean {
  if (!field.when) return true;
  return Object.entries(field.when).every(([key, allowed]) =>
    allowed.includes(String(values[key] ?? "")),
  );
}

export function CurveOpsDialog({
  project,
  well,
  operation: operationKey,
  onClose,
  onApplied,
}: {
  project: Project;
  well: Well;
  operation: string;
  onClose: () => void;
  onApplied: () => void;
}) {
  const toast = useToast();
  const catalogue = useAsync(
    (signal) => api.get<CurveOpCatalogue>("/api/curve-ops/", undefined, signal),
    [],
  );
  const operation = catalogue.data?.operations.find((item) => item.key === operationKey);
  const choices = useMemo(() => curveChoices(well), [well]);

  const [values, setValues] = useState<Values | null>(null);
  const [applying, setApplying] = useState(false);

  // The form is seeded once the declaration arrives, and re-seeded when the
  // well changes: a curve reference from another well resolves to nothing.
  useEffect(() => {
    if (operation) setValues(initialValues(operation, choices));
  }, [operation, choices]);

  const settled = useDebounced(values, 300);
  const missing = operation && values ? missingRequired(operation, values, choices) : [];
  const settledMissing = operation && settled
    ? missingRequired(operation, settled, choices)
    : ["—"];
  const preview = useAsync<CurveOpResult | null>(
    async () => {
      if (!operation || !settled) return null;
      return api.post<CurveOpResult>("/api/curve-ops/run/", {
        well: well.id,
        operation: operation.key,
        preview: true,
        params: settled,
      });
    },
    [operation?.key, settled, well.id],
    { skip: settledMissing.length > 0 },
  );

  const mayApply = canCompute(project.my_role);

  const apply = async () => {
    if (!operation || !values) return;
    setApplying(true);
    try {
      const result = await api.post<CurveOpResult>("/api/curve-ops/run/", {
        well: well.id,
        operation: operation.key,
        params: values,
      });
      const names = result.outputs.map((output) => output.mnemonic).join(", ");
      toast(
        `${result.label}: ${names} → ${result.curve_set?.name} v${result.curve_set?.version}`,
      );
      onApplied();
      onClose();
    } catch (cause) {
      toast(cause instanceof Error ? cause.message : String(cause), "error");
    } finally {
      setApplying(false);
    }
  };

  const set = (name: string, value: unknown) =>
    setValues((current) => ({ ...(current ?? {}), [name]: value }));

  const title = operation ? `${operation.group_label} — ${operation.label}` : "Operasi kurva";

  return (
    <Modal
      title={title}
      onClose={onClose}
      width={860}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            busy={applying}
            disabled={!mayApply || !operation || missing.length > 0 || !!preview.error}
            title={
              mayApply
                ? undefined
                : "Only owners and petrophysicists may edit curves"
            }
            onClick={apply}
          >
            Apply
          </Button>
        </>
      }
    >
      {catalogue.loading && <Loading label="Loading the operation list…" />}
      {catalogue.error && <Notice tone="error">{catalogue.error}</Notice>}
      {catalogue.data && !operation && (
        <Notice tone="error">Operasi “{operationKey}” tidak ada di katalog server.</Notice>
      )}

      {operation && values && (
        <div style={{ display: "flex", gap: 13, minHeight: 0, alignItems: "flex-start" }}>
          <div
            className="scroll-y"
            style={{
              width: 330,
              flex: "none",
              display: "flex",
              flexDirection: "column",
              gap: 9,
              maxHeight: 470,
              paddingRight: 4,
            }}
          >
            <span style={{ font: "400 10.5px/1.5 var(--font-sans)", color: "var(--ink-4)" }}>
              {operation.summary}
            </span>
            {operation.warning && <Notice tone="warn">{operation.warning}</Notice>}
            {choices.length === 0 && needsExistingCurve(operation, values) && (
              <Notice tone="error">
                {well.name} belum punya kurva. Curve insert bisa membuat yang pertama;
                this operation needs a curve that already exists.
              </Notice>
            )}
            <Fields operation={operation} values={values} choices={choices} onChange={set} />
          </div>

          <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 9 }}>
            {missing.length > 0 ? (
              <Empty>Isi dulu: {missing.join("; ")}.</Empty>
            ) : (
              <PreviewPane
                preview={preview.data ?? null}
                loading={preview.loading}
                error={preview.error}
                wellName={well.name}
              />
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}

function Fields({
  operation,
  values,
  choices,
  onChange,
}: {
  operation: CurveOperation;
  values: Values;
  choices: CurveChoice[];
  onChange: (name: string, value: unknown) => void;
}) {
  let section = "";
  const rendered = [];
  for (const field of operation.fields) {
    if (!visible(field, values)) continue;
    const heading = field.section ?? "";
    if (heading !== section) {
      section = heading;
      if (heading) {
        rendered.push(
          <span
            key={`section-${heading}`}
            style={{
              font: "600 9.5px var(--font-mono)",
              color: "var(--ink-6)",
              letterSpacing: ".08em",
              marginTop: 4,
            }}
          >
            {heading.toUpperCase()}
          </span>,
        );
      }
    }
    rendered.push(
      <Field
        key={field.name}
        field={field}
        value={values[field.name]}
        choices={choices}
        onChange={(next) => onChange(field.name, next)}
      />,
    );
  }
  return <>{rendered}</>;
}

function Field({
  field,
  value,
  choices,
  onChange,
}: {
  field: CurveOpField;
  value: unknown;
  choices: CurveChoice[];
  onChange: (value: unknown) => void;
}) {
  const help = field.help ? (
    <span style={{ font: "400 9.5px/1.45 var(--font-sans)", color: "var(--ink-6)" }}>
      {field.help}
    </span>
  ) : null;

  const body = () => {
    switch (field.type) {
      case "curve":
        return (
          <Dropdown
            label={field.label}
            value={String(value ?? "")}
            options={choices.map((choice) => ({ value: choice.value, label: choice.label }))}
            onChange={onChange}
            width="100%"
            compact
          />
        );
      case "curves":
        return (
          <CurveList
            label={field.label}
            selected={(value as string[]) ?? []}
            choices={choices}
            onChange={onChange}
          />
        );
      case "choice":
        return (
          <Dropdown
            label={field.label}
            value={String(value ?? "")}
            options={(field.options ?? []).map((option) => ({
              value: option.value,
              label: option.label,
              hint: option.hint,
            }))}
            onChange={onChange}
            width="100%"
            compact
          />
        );
      case "number":
        return field.optional ? (
          <OptionalNumberField
            label={field.label}
            unit={field.unit}
            value={value === null || value === undefined ? null : Number(value)}
            step={field.step}
            min={field.min}
            max={field.max}
            onChange={onChange}
          />
        ) : (
          <NumberField
            label={field.label}
            unit={field.unit}
            value={Number(value ?? 0)}
            step={field.step}
            min={field.min}
            max={field.max}
            onChange={onChange}
          />
        );
      case "bool":
        return (
          <Checkbox checked={Boolean(value)} onChange={onChange} label={field.label} />
        );
      case "ties":
        return (
          <TieTable
            label={field.label}
            columns={field.columns ?? []}
            rows={(value as Tie[]) ?? []}
            onChange={onChange}
          />
        );
      default:
        return (
          <TextField
            label={field.label}
            value={String(value ?? "")}
            placeholder={field.placeholder}
            onChange={onChange}
          />
        );
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      {body()}
      {help}
    </div>
  );
}

/**
 * Daftar kurva yang urutannya bisa diatur.
 *
 * Urutan bukan hiasan pada penggabungan: yang di atas menang pada tumpang
 * tindih. Kotak centang biasa tidak punya urutan sama sekali, jadi ia tidak
 * bisa menyatakan run mana yang lebih dipercaya.
 */
function CurveList({
  label,
  selected,
  choices,
  onChange,
}: {
  label: string;
  selected: string[];
  choices: CurveChoice[];
  onChange: (value: string[]) => void;
}) {
  const remaining = choices.filter((choice) => !selected.includes(choice.value));
  const move = (index: number, by: number) => {
    const next = [...selected];
    const target = index + by;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    onChange(next);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5, minWidth: 0 }}>
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>{label}</span>
      {selected.map((value, index) => (
        <div key={value} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <span
            style={{
              width: 15,
              flex: "none",
              font: "400 9px var(--font-mono)",
              color: "var(--ink-6)",
            }}
          >
            {index + 1}.
          </span>
          <span
            style={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              font: "500 11px var(--font-sans)",
              color: "var(--ink-2)",
            }}
          >
            {choices.find((choice) => choice.value === value)?.label ?? value}
          </span>
          <MiniButton label="Move up" onClick={() => move(index, -1)}>
            ↑
          </MiniButton>
          <MiniButton label="Move down" onClick={() => move(index, 1)}>
            ↓
          </MiniButton>
          <MiniButton
            label="Take out"
            onClick={() => onChange(selected.filter((item) => item !== value))}
          >
            ✕
          </MiniButton>
        </div>
      ))}
      {remaining.length > 0 && (
        <Dropdown
          value=""
          options={[
            { value: "", label: "Add curve…" },
            ...remaining.map((choice) => ({ value: choice.value, label: choice.label })),
          ]}
          onChange={(value) => value && onChange([...selected, value])}
          width="100%"
          compact
        />
      )}
    </div>
  );
}

function MiniButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: string;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      onClick={onClick}
      style={{
        width: 19,
        height: 19,
        flex: "none",
        padding: 0,
        background: "var(--control-flat)",
        border: "1px solid var(--border-control)",
        borderRadius: 4,
        color: "var(--ink-4)",
        font: "400 10px var(--font-mono)",
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function TieTable({
  label,
  columns,
  rows,
  onChange,
}: {
  label: string;
  columns: { name: string; label: string }[];
  rows: Tie[];
  onChange: (rows: Tie[]) => void;
}) {
  const edit = (index: number, key: string, text: string) => {
    const next = rows.map((row, position) =>
      position === index ? { ...row, [key]: text } : row,
    );
    onChange(next);
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <span style={{ font: "400 10px var(--font-mono)", color: "var(--ink-5)" }}>{label}</span>
      <div style={{ display: "flex", gap: 5, font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>
        {columns.map((column) => (
          <span key={column.name} style={{ flex: 1 }}>
            {column.label}
          </span>
        ))}
        <span style={{ width: 19, flex: "none" }} />
      </div>
      {rows.map((row, index) => (
        <div key={index} style={{ display: "flex", alignItems: "center", gap: 5 }}>
          {columns.map((column) => (
            <input
              key={column.name}
              type="number"
              step="0.01"
              aria-label={`${column.label} ${index + 1}`}
              value={String((row as Record<string, string>)[column.name] ?? "")}
              onChange={(event) => edit(index, column.name, event.target.value)}
              style={{
                flex: 1,
                minWidth: 0,
                height: 26,
                padding: "0 7px",
                background: "var(--panel-inset)",
                border: "1px solid var(--line-strong)",
                borderRadius: 5,
                font: "500 11px var(--font-mono)",
                color: "var(--ink)",
                outline: "none",
              }}
            />
          ))}
          <MiniButton
            label="Delete anchor point"
            onClick={() => onChange(rows.filter((_, position) => position !== index))}
          >
            ✕
          </MiniButton>
        </div>
      ))}
      <Button variant="ghost" onClick={() => onChange([...rows, { from: "", to: "" }])}>
        + Anchor point
      </Button>
    </div>
  );
}

function PreviewPane({
  preview,
  loading,
  error,
  wellName,
}: {
  preview: CurveOpResult | null;
  loading: boolean;
  error: string | null;
  wellName: string;
}) {
  if (error) return <Notice tone="error">{error}</Notice>;
  if (loading && !preview) return <Loading label="Menghitung pratinjau…" />;
  if (!preview) return <Empty>Pick a curve to see its preview.</Empty>;

  return (
    <>
      {preview.messages.map((message, index) => (
        <Notice key={index} tone="warn">
          {message}
        </Notice>
      ))}
      <div
        className="scroll-y"
        style={{ display: "flex", flexDirection: "column", gap: 9, maxHeight: 400 }}
      >
        {preview.outputs.map((output) => (
          <Trace key={output.mnemonic} output={output} wellName={wellName} />
        ))}
      </div>
      <Panel title="Summary" bodyStyle={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 16px" }}>
        <KeyValue k="Written to" v={`${preview.target_set.name} v${preview.target_set.version + 1}`} />
        {Object.entries(preview.stats).map(([key, value]) => (
          <KeyValue key={key} k={statLabel(key)} v={statValue(value)} />
        ))}
      </Panel>
    </>
  );
}

const STAT_LABELS: Record<string, string> = {
  spikes: "Spikes removed",
  samples: "Samples",
  largest_change: "Largest change",
  min_shift: "Smallest shift",
  max_shift: "Largest shift",
  min_correction: "Smallest correction",
  max_correction: "Largest correction",
  min_factor: "Smallest factor",
  max_factor: "Largest factor",
  min_ratio: "Smallest ratio",
  max_ratio: "Largest ratio",
  covered: "Covered",
  overlapping: "Overlapping",
  step: "Step",
  pieces: "Pieces",
  boundaries: "Boundaries",
  curves: "Curves",
  nulled: "Blanked",
  remaining: "Remaining",
  filled: "Filled",
  reconstructed: "Reconstructed",
  fitted_samples: "Fitted samples",
  r2: "R²",
  rmse: "RMSE",
  coefficients: "Coefficients",
  method: "Method",
  units: "Units",
  direction: "Direction",
  expression: "Expression",
  trajectory: "Trajectory",
  kb_elevation: "KB elevation",
  gl_elevation: "Ground elevation",
  suspect_samples: "Suspect samples",
  constant: "Arps constant",
  max_gap_samples: "Largest gap",
  formation_temperature_at_td: "Temperature at TD",
};

function statLabel(key: string): string {
  return STAT_LABELS[key] ?? key.replace(/_/g, " ");
}

function statValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (Array.isArray(value)) return value.map((item) => statValue(item)).join(", ");
  if (typeof value === "number") {
    if (Number.isInteger(value)) return value.toLocaleString("en-US");
    return value.toFixed(Math.abs(value) < 0.01 ? 5 : 3);
  }
  return String(value);
}

/**
 * Kurva sebelum dan sesudah, di atas sumbu kedalaman yang sama.
 *
 * Keduanya digambar pada skala yang sama dan dari data yang sama-sama
 * dijarangkan, supaya perbedaannya adalah perbedaan hitungan — bukan
 * perbedaan cara menggambar. Yang lama putus-putus dan pucat; yang baru
 * berwarna aksen dan di atasnya.
 */
function Trace({ output, wellName }: { output: CurveOpOutput; wellName: string }) {
  const plot = useMemo(() => {
    const depths = output.depths.map((depth) => Number(depth));
    const finite: number[] = [];
    for (const series of [output.values, output.previous ?? []]) {
      for (const value of series) if (value !== null && Number.isFinite(value)) finite.push(value);
    }
    if (!depths.length || !finite.length) return null;
    let low = Math.min(...finite);
    let high = Math.max(...finite);
    if (high - low < 1e-9) {
      // A constant curve has no range to scale to; centring it keeps the line
      // visible instead of collapsing it onto the top edge.
      low -= 1;
      high += 1;
    }
    const top = depths[0];
    const base = depths[depths.length - 1];
    const span = base - top || 1;
    const path = (series: (number | null)[]) => {
      let d = "";
      let open = false;
      series.forEach((value, index) => {
        if (value === null || !Number.isFinite(value)) {
          open = false;
          return;
        }
        const x = ((value - low) / (high - low)) * 100;
        const y = ((depths[index] - top) / span) * 100;
        d += `${open ? "L" : "M"}${x.toFixed(2)} ${y.toFixed(2)} `;
        open = true;
      });
      return d.trim();
    };
    return {
      top,
      base,
      low,
      high,
      now: path(output.values),
      before: output.previous ? path(output.previous) : "",
    };
  }, [output]);

  return (
    <Panel
      title={
        <span style={{ display: "flex", alignItems: "baseline", gap: 7 }}>
          <span>{output.mnemonic}</span>
          <span style={{ font: "400 9px var(--font-mono)", color: "var(--ink-6)" }}>
            {output.unit || "—"} · {output.replaces ? "overwrites the existing one" : "a new curve"}
          </span>
        </span>
      }
      meta={`${output.count.toLocaleString("en-US")} samples @ ${output.depth_step.toFixed(4)} m`}
    >
      {!plot ? (
        <Empty>The result carries no numeric value at all.</Empty>
      ) : (
        <>
          <div
            style={{
              position: "relative",
              height: 150,
              background: "var(--canvas-deep)",
              border: "1px solid var(--line-strong)",
              borderRadius: 5,
              overflow: "hidden",
            }}
          >
            <svg
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
              aria-label={`${output.mnemonic} on ${wellName}`}
            >
              {plot.before && (
                <path
                  d={plot.before}
                  fill="none"
                  stroke="var(--ink-5)"
                  strokeWidth={1}
                  strokeDasharray="3 2"
                  vectorEffect="non-scaling-stroke"
                />
              )}
              <path
                d={plot.now}
                fill="none"
                stroke="var(--accent)"
                strokeWidth={1.2}
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              font: "400 9px var(--font-mono)",
              color: "var(--ink-6)",
            }}
          >
            <span>{format(plot.low)}</span>
            <span>
              {plot.top.toFixed(0)}–{plot.base.toFixed(0)} m
              {plot.before ? " · -- before, — after" : ""}
            </span>
            <span>{format(plot.high)}</span>
          </div>
        </>
      )}
    </Panel>
  );
}

function format(value: number): string {
  if (Math.abs(value) >= 1000) return value.toFixed(0);
  if (Math.abs(value) >= 1) return value.toFixed(2);
  return value.toFixed(4);
}
